/**
 * Simulated `/api/hr-dates` — the birthdays / work anniversaries / new hires
 * the HR tab and TV slide 5 show.
 *
 * `profiles` in this snapshot has NO date-of-birth and NO hire-date column, so
 * both lists are generated deterministically from each person's REAL name (a
 * fixed seed per name, so the same person keeps the same date on every reload).
 * The names, and who is active, are real. Both are badged SIMULATED on screen.
 */
import { loadTable } from '~/demo/data';
import { once, seededInt } from '~/demo/simulated/prng';
import { nyDate, str } from '~/demo/simulated/finance';

export interface HrDateRow {
  name: string;
  /** 'MM-DD'. */
  monthDay: string;
  /** Only for anniversaries. */
  years?: number;
}
export interface HrDatesPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  month: number;
  birthdays: HrDateRow[];
  anniversaries: HrDateRow[];
  welcomeSoon: { name: string; startDate: string; role: string }[];
  missingFields: string[];
}

const ROLES = ['Water Technician', 'Contents Technician', 'Project Coordinator', 'Estimator'] as const;

export const simulatedHrDates = once(async (): Promise<HrDatesPayload> => {
  const profiles = await loadTable('profiles');
  const today = nyDate();
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));

  const active = profiles
    .filter((p) => p.is_active !== false)
    .map((p) => str(p.display_name) || str(p.email))
    .filter(Boolean)
    .sort();

  const birthdays: HrDateRow[] = [];
  const anniversaries: HrDateRow[] = [];
  for (const name of active) {
    const bMonth = seededInt(`dob|m|${name}`, 1, 12);
    if (bMonth === month) {
      birthdays.push({ name, monthDay: `${String(bMonth).padStart(2, '0')}-${String(seededInt(`dob|d|${name}`, 1, 28)).padStart(2, '0')}` });
    }
    const hMonth = seededInt(`hire|m|${name}`, 1, 12);
    if (hMonth === month) {
      const years = year - (year - seededInt(`hire|y|${name}`, 1, 9));
      anniversaries.push({
        name,
        monthDay: `${String(hMonth).padStart(2, '0')}-${String(seededInt(`hire|d|${name}`, 1, 28)).padStart(2, '0')}`,
        years,
      });
    }
  }
  birthdays.sort((a, b) => a.monthDay.localeCompare(b.monthDay));
  anniversaries.sort((a, b) => a.monthDay.localeCompare(b.monthDay));

  // "Welcome Soon" — a new-hire block. Nothing in the snapshot models a pending
  // hire, so this is empty unless a profile was created in the last 30 days.
  const cutoff = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const welcomeSoon = profiles
    .filter((p) => p.is_active !== false && str(p.created_at) >= cutoff)
    .slice(0, 4)
    .map((p) => ({
      name: str(p.display_name) || str(p.email),
      startDate: str(p.created_at).slice(0, 10),
      role: ROLES[seededInt(`role|${str(p.display_name)}`, 0, ROLES.length - 1)],
    }));

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Names and active status are real (profiles). `profiles` has no date_of_birth and no hire_date column, so '
      + 'each person\'s birthday and work anniversary are generated from a fixed seed on their name — stable across '
      + 'reloads, but not their real dates. "Welcome Soon" lists profiles created in the last 30 days.',
    month,
    birthdays,
    anniversaries,
    welcomeSoon,
    missingFields: ['profiles.date_of_birth', 'profiles.hire_date'],
  };
});

/**
 * Spec §6 slide 5 — the Joke of the Day bank. Picked by DAY-OF-YEAR so the
 * whole office sees the same joke for the whole day and it changes at midnight.
 */
export const JOKE_BANK: readonly { setup: string; punch: string }[] = Object.freeze([
  { setup: 'I have an irrational fear of speed bumps,', punch: "but I'm slowly getting over it." },
  { setup: 'I told my wife she was drawing her eyebrows too high.', punch: 'She looked surprised.' },
  { setup: "Why don't scientists trust atoms?", punch: 'Because they make up everything.' },
  { setup: 'I used to hate facial hair,', punch: 'but then it grew on me.' },
  { setup: 'What do you call fake spaghetti?', punch: 'An impasta.' },
  { setup: 'I only know 25 letters of the alphabet.', punch: "I don't know y." },
  { setup: 'Why did the scarecrow win an award?', punch: 'He was outstanding in his field.' },
  { setup: 'I would tell you a construction joke,', punch: "but I'm still working on it." },
  { setup: 'Why did the water damage tech bring a ladder?', punch: 'He heard the job had high ceilings.' },
  { setup: 'What do you call a dehumidifier that sings?', punch: 'A dry-mer.' },
  { setup: 'My boss told me to have a good day.', punch: 'So I went home.' },
  { setup: 'I bought some shoes from a drug dealer.', punch: "I don't know what he laced them with." },
  { setup: 'Why do painters always lose at hide and seek?', punch: 'They leave a trail.' },
  { setup: 'What did the wall say to the other wall?', punch: "I'll meet you at the corner." },
  { setup: 'Why was the mold inspector so calm?', punch: 'Nothing ever got under his skin.' },
  { setup: 'I tried to catch fog yesterday.', punch: 'Mist.' },
  { setup: 'What do you call an alligator in a vest?', punch: 'An investigator.' },
  { setup: "Don't trust stairs.", punch: "They're always up to something." },
  { setup: 'Why did the drywall go to therapy?', punch: 'It had too many issues to patch up.' },
  { setup: 'How do you organize a space party?', punch: 'You planet.' },
  { setup: 'I used to be a banker,', punch: 'but I lost interest.' },
  { setup: 'What kind of shoes does a plumber hate?', punch: 'Clogs.' },
  { setup: 'Why did the truck get a promotion?', punch: 'It was outstanding in its fleet.' },
  { setup: 'I ordered a chicken and an egg online.', punch: "I'll let you know." },
  { setup: 'Why are elevator jokes so good?', punch: 'They work on so many levels.' },
  { setup: 'What did the roof say to the shingle?', punch: "You've got me covered." },
  { setup: 'I told a joke about a fan.', punch: 'Nobody was blown away.' },
  { setup: 'Why did the carpenter bring a pencil to dinner?', punch: 'To draw the check.' },
  { setup: 'Parallel lines have so much in common.', punch: "It's a shame they'll never meet." },
  { setup: 'How does a restoration tech greet you?', punch: '"Water you doing later?"' },
  { setup: 'I named my dog Five Miles,', punch: 'so I can say I walk five miles every day.' },
  { setup: 'Why did the invoice go to the gym?', punch: 'It wanted to get in better shape before month-end.' },
  { setup: 'What do you call a pile of cats?', punch: 'A meowtain.' },
  { setup: 'My wifi went out during a storm.', punch: "That's a disaster we don't restore." },
  { setup: 'Why did the estimator bring a mirror?', punch: 'To reflect on the scope.' },
  { setup: 'I would avoid the sushi if I were you.', punch: "It's a little fishy." },
  { setup: 'Why did the tape measure get invited everywhere?', punch: 'It always measured up.' },
  { setup: 'What did the air mover say to the wet floor?', punch: '"Dry harder."' },
  { setup: 'I have a joke about time travel,', punch: "but you didn't like it." },
  { setup: 'Why do dispatchers make great friends?', punch: "They're always there when you call." },
  { setup: 'What do you call a boomerang that never comes back?', punch: 'A stick.' },
  { setup: 'Why did the safety cone blush?', punch: 'It saw the floor plan.' },
  { setup: 'I asked the ladder for advice.', punch: 'It told me to step up.' },
  { setup: 'What do you call a fake noodle at a job site?', punch: 'An impasta drain line.' },
  { setup: 'Why did the crew love Fridays?', punch: 'The week finally dried out.' },
]);

/** Spec §6 — the joke for a given day, stable for the whole day. */
export function jokeOfTheDay(now: Date = new Date()): { setup: string; punch: string } {
  const start = Date.UTC(now.getUTCFullYear(), 0, 0);
  const doy = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - start) / 86_400_000);
  return JOKE_BANK[((doy % JOKE_BANK.length) + JOKE_BANK.length) % JOKE_BANK.length];
}
